import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, interval, Observable, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export type LMS_NotificationType = 'info' | 'success' | 'warning' | 'error' | 'none';


interface LMSHighlightEvent {
  type: LMS_NotificationType;
  target: string; // e.g. an element ID, component key, or class name
  message: string;
}


@Injectable()
export class NotificationService {
  private readonly pollingInterval = 30000; // 30 seconds
  private subscriptions: Subscription[];
  statusAnimationState: 'normal' | 'pulsed' = 'normal';
  private previousHashes = new Map<number, string>(); // id → hash
  private quote_apiURL = `${environment.apiUrl}/loan/api/quote/quickSearch/awaiting`;
  private _isQuoteFound = false;

  private ads = [{
      title: '',
      body: '',
      options: { closeButton: true, tapToDismiss: false, progressBar: true}
    }];

  constructor(private http: HttpClient) {
    this.subscriptions = [];
  }

  private notificationSubject = new Subject<LMSHighlightEvent>();
  stream$ = this.notificationSubject.asObservable();

  private badgeCount$ = new BehaviorSubject<number>(0);
  readonly badge$ = this.badgeCount$.asObservable();


  setBadgeCount(count: number) {
    this.badgeCount$.next(count);
  }

  clearBadge() {
    this.badgeCount$.next(0);
  }


  notify(event: LMSHighlightEvent) {
    this.notificationSubject.next(event);
  }

  notifytarget(event: LMSHighlightEvent) {
    document.querySelectorAll(`.panel-item.${event.target}`)?.forEach(el => {
      el.classList.add('animate-highlight');
      setTimeout(() => el.classList.remove('animate-highlight'), 1000);
    });
  }

  startPolling(endpoint: string, callback: (events: any[]) => void): void {
    const sub = interval(this.pollingInterval).pipe(takeUntil(this.destroy$),switchMap(() => this.http.get<any[]>(endpoint)));
    const subscribe = sub.subscribe({
        next: events => callback(events),
        error: err => console.error('Polling error:', err)
      });

    this.subscriptions.push(subscribe);
  }
  private destroy$ = new Subject<void>();
  private items$ = new BehaviorSubject<any[]>([]);
  readonly streamitems$ = this.items$.asObservable();
  private isPollingActive = false;

  public startQuotePolling(onStatusChange: (quotes: any) => void): void {
    // Prevent multiple concurrent polling subscriptions
    if (this.isPollingActive) {
      console.warn('Quote polling is already active');
      return;
    }

    this.isPollingActive = true;
    console.log('Starting quote polling for AMAwaiting quotes...');

    const subscription = interval(this.pollingInterval).pipe(
      takeUntil(this.destroy$),
      switchMap(() => this.http.get<any>(this.quote_apiURL)),
      tap((items: any) => {
        if (items && items.length > 0) {
          console.log(`Found ${items.length} awaiting quote(s)`);
        }
        onStatusChange(items);
      })
    ).subscribe({
      error: (err) => {
        console.error('Quote polling failed:', err);
        this.isPollingActive = false;
      },
      complete: () => {
        console.log('Quote polling stopped');
        this.isPollingActive = false;
      }
    });

    this.subscriptions.push(subscription);
  }
  private adsSubject = new BehaviorSubject<any[]>(this.ads);

  getAds(): Observable<any[]> {
    return this.adsSubject.asObservable();
  }

  setAds(ads: any[]) {
    this.ads = ads;
    this.adsSubject.next(ads);
  }

  stopPolling(): void {
    console.log('Stopping all polling subscriptions...');

    // Signal all takeUntil operators to complete
    this.destroy$.next();
    this.destroy$.complete();

    // Unsubscribe from all tracked subscriptions
    if (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.forEach(sub => sub.unsubscribe());
      this.subscriptions = [];
    }

    // Reset state
    this.isPollingActive = false;
    this.previousHashes.clear();

    // Recreate destroy$ for potential restart
    this.destroy$ = new Subject<void>();
  }
}
